/*
  <scroll-carousel> — the slider used by the Loop-direction sections
  (image-cards-slider, campaign-slider, media-cards).

  The track is a native horizontal scroller with CSS scroll-snap, so it swipes, drags on
  trackpads and stays usable if this script never runs. The script only adds what CSS
  cannot: prev/next buttons, a pagination row, optional autoplay with a pause toggle, and
  their active/disabled states.

  It deliberately does not use the theme's Splide: that instance lives in the remote bundle
  (js.shrinetheme.com) and is not reachable from outside — see HOMEPAGE-RESTRUCTURE.md §24.

  Markup contract (parts in [] are optional):
    <scroll-carousel data-pagination="dots|numbers|none" [data-bleed]
                     [data-autoplay="SECONDS"] [data-loop] data-slide-label="Load slide">
      <div class="scroll-carousel__viewport">        holds the track and the edge arrows
        <ul class="scroll-carousel__track" data-carousel-track> <li>…</li> … </ul>
        [<button class="scroll-carousel__arrow scroll-carousel__arrow--prev [color-SCHEME]" data-carousel-prev>]
        [<button class="scroll-carousel__arrow scroll-carousel__arrow--next [color-SCHEME]" data-carousel-next>]
      </div>
      [<div class="scroll-carousel__dots" data-carousel-dots></div>]
      or, when there is a toggle:
      [<div class="scroll-carousel__controls">
         [<div class="scroll-carousel__dots" data-carousel-dots></div>]
         <button class="scroll-carousel__toggle" data-carousel-toggle
                 data-label-pause="Pause slideshow" data-label-play="Play slideshow">
       </div>]
    </scroll-carousel>

  data-autoplay advances one stop every SECONDS and wraps from the last stop to the first,
  so it implies data-loop; data-loop alone makes the arrows wrap instead of disabling at the
  ends. Autoplay holds while the pointer or keyboard focus is on the slides, while the tab is
  hidden and while a block is selected in the Theme Editor, and it starts paused for visitors
  who ask for reduced motion. The toggle is its WCAG 2.2.2 pause control.

  In the Theme Editor, selecting a slide's block scrolls the track to it. Autoplaying videos
  inside the slides play only while in view, and never for reduced-motion visitors.
*/
if (!customElements.get('scroll-carousel')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      // One set of listeners per connection. A section reorder in the editor disconnects and
      // reconnects the element; without this every arrow gained a second click listener and
      // stepped twice per click.
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.dots = this.querySelector('[data-carousel-dots]');
      this.prev = [...this.querySelectorAll('[data-carousel-prev]')];
      this.next = [...this.querySelectorAll('[data-carousel-next]')];
      this.toggle = this.querySelector('[data-carousel-toggle]');

      const seconds = parseFloat(this.dataset.autoplay);
      this.autoplayMs = seconds > 0 ? seconds * 1000 : 0;
      this.loop = this.hasAttribute('data-loop') || this.autoplayMs > 0;
      this.holds = new Set();
      this.userPaused = reducedMotion.matches;

      this.prev.forEach((button) => button.addEventListener('click', () => this.step(-1, true), { signal }));
      this.next.forEach((button) => button.addEventListener('click', () => this.step(1, true), { signal }));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });

      if (this.autoplayMs) this.setupAutoplay(signal);
      if (window.Shopify && window.Shopify.designMode) this.setupEditor(signal);
      this.setupVideos();

      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
      this.build();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.stopTimer();
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    // One stop per distinct position a slide can snap to. Slides near the end all clamp to
    // maxScroll, so seven cards at four per view give four stops, not seven — the same
    // collapse Loop's pagination does. Slide i therefore snaps to stop i, clamped to the last.
    //
    // Stops are measured from the first slide, not from getComputedStyle().scrollPadding:
    // the bleed inset is a max()/percentage expression and Chrome returns it unresolved
    // ("max(15px, 50% - 685px)"), which parses to NaN. Reading it that way put every target
    // one inset past its snap point, and mandatory snapping then carried the scroll on to the
    // NEXT stop — clicking "2" landed on "3". The first slide sits exactly at the inset, so
    // measuring from it gives the snap positions with no style parsing at all.
    stops() {
      const slides = [...this.track.children];
      const origin = slides[0]?.offsetLeft || 0;
      const max = this.maxScroll;
      const stops = [];
      for (const slide of slides) {
        const position = Math.min(Math.max(slide.offsetLeft - origin, 0), max);
        if (!stops.length || position - stops[stops.length - 1] > 2) stops.push(position);
        if (position >= max) break;
      }
      return stops.length ? stops : [0];
    }

    build() {
      this.positions = this.stops();
      this.toggleAttribute('data-static', this.positions.length < 2);

      // Rebuild the buttons only when the number of stops changes. The observer also fires on
      // resize, zoom and the editor's device switch, and replacing the buttons every time threw
      // away a focused dot. A button reads this.positions when clicked, so it stays right.
      if (this.dots && this.dots.children.length !== this.positions.length) {
        const numbered = this.dataset.pagination === 'numbers';
        this.dots.replaceChildren(
          ...this.positions.map((_, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'scroll-carousel__dot';
            button.setAttribute('aria-label', `${this.dataset.slideLabel || 'Go to slide'} ${i + 1}`);
            if (numbered) button.textContent = i + 1;
            button.addEventListener('click', () => {
              this.go(i);
              this.restart();
            });
            return button;
          })
        );
      }
      this.update();
      this.restart();
    }

    go(i) {
      const target = this.positions[Math.max(0, Math.min(this.positions.length - 1, i))];
      // An explicit behavior overrides CSS scroll-behavior, so reduced motion is honoured here:
      // the CSS rule alone never reached these calls.
      this.track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    step(delta, fromUser) {
      const last = this.positions.length - 1;
      let i = this.index + delta;
      if (this.loop) i = i > last ? 0 : i < 0 ? last : i;
      this.go(i);
      if (fromUser) this.restart();
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const x = this.track.scrollLeft;
      let index = 0;
      this.positions.forEach((position, i) => {
        if (Math.abs(position - x) < Math.abs(this.positions[index] - x)) index = i;
      });
      this.index = index;

      if (this.dots) {
        [...this.dots.children].forEach((dot, i) => {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      const atStart = !this.loop && x <= 1;
      const atEnd = !this.loop && x >= this.maxScroll - 1;
      // A disabled arrow is invisible and would drop keyboard focus to <body>: enable the arrow
      // that stays usable, move focus onto it, and only then disable the one at its end.
      if (!atStart) this.prev.forEach((button) => (button.disabled = false));
      if (!atEnd) this.next.forEach((button) => (button.disabled = false));
      const focused = document.activeElement;
      if (atStart && this.prev.includes(focused)) this.next.find((button) => !button.disabled)?.focus({ preventScroll: true });
      if (atEnd && this.next.includes(focused)) this.prev.find((button) => !button.disabled)?.focus({ preventScroll: true });
      this.prev.forEach((button) => (button.disabled = atStart));
      this.next.forEach((button) => (button.disabled = atEnd));
    }

    setupAutoplay(signal) {
      const hold = (reason, on) => {
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        this.startTimer();
      };
      // Hold on the slides only, not on the controls: hovering or focusing the toggle to press
      // play must not keep the carousel paused. Pointer events, not mouse events: a tap on a
      // phone fires a compatibility mouseenter with no mouseleave until the next tap elsewhere,
      // which held autoplay indefinitely; pointerleave always follows a touch's pointerup.
      this.track.addEventListener('pointerenter', () => hold('hover', true), { signal });
      this.track.addEventListener('pointerleave', () => hold('hover', false), { signal });
      this.track.addEventListener('focusin', () => hold('focus', true), { signal });
      this.track.addEventListener(
        'focusout',
        (event) => {
          if (!this.track.contains(event.relatedTarget)) hold('focus', false);
        },
        { signal }
      );
      document.addEventListener('visibilitychange', () => hold('hidden', document.hidden), { signal });
      if (document.hidden) this.holds.add('hidden'); // page opened in a background tab

      if (this.toggle) {
        this.toggle.addEventListener(
          'click',
          () => {
            this.userPaused = !this.userPaused;
            this.renderToggle();
            this.startTimer();
          },
          { signal }
        );
        this.renderToggle();
      }
    }

    renderToggle() {
      const paused = this.userPaused;
      this.toggle.classList.toggle('is-paused', paused);
      const label = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
      if (label) this.toggle.setAttribute('aria-label', label);
    }

    startTimer() {
      this.stopTimer();
      if (!this.autoplayMs || this.userPaused || this.holds.size || this.hasAttribute('data-static')) return;
      this.timer = setInterval(() => this.step(1, false), this.autoplayMs);
    }

    stopTimer() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      if (this.autoplayMs) this.startTimer();
    }

    setupEditor(signal) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const i = [...this.track.children].findIndex((slide) => slide === event.target || slide.contains(event.target));
          if (i < 0) return;
          this.holds.add('editor');
          this.startTimer();
          this.go(i); // go() clamps: slides past the last stop share it
        },
        { signal }
      );
      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.holds.delete('editor');
          this.startTimer();
        },
        { signal }
      );
    }

    setupVideos() {
      this.videoObserver?.disconnect();
      const videos = [...this.track.querySelectorAll('video[autoplay]')];
      if (!videos.length) return;
      if (!('IntersectionObserver' in window)) {
        if (reducedMotion.matches) videos.forEach((video) => video.pause());
        return;
      }
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting && !reducedMotion.matches) target.play()?.catch(() => {});
            else target.pause();
          });
        },
        { threshold: 0.25 }
      );
      videos.forEach((video) => this.videoObserver.observe(video));
    }
  }

  customElements.define('scroll-carousel', ScrollCarousel);
}
